import { Component, inject, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { IlsModelService, IlsStatus } from '../shared/services/ils-model.service'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { Title } from '@angular/platform-browser'
import { SendLogActionService } from '../shared/services/send-log-action.service'
import { MatDrawer, MatDrawerContainer } from '@angular/material/sidenav'
import { CachingStorageService } from '../shared/services/caching-storage.service'
import { TokenService } from '@monorepo/auth'
import { TitleBarComponent } from './title-bar/title-bar.component'
import { PhaseListComponent } from './phase-list/phase-list.component'
import { AsyncPipe, NgClass } from '@angular/common'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatTooltip } from '@angular/material/tooltip'
import { Content } from '@monorepo/golab-generated'
import { PhaseContentComponent } from './phase-content/phase-content.component'
import { StudentGroupContentService } from '../shared/services/student-group-content.service'
import { createStudentGroupContent } from '../shared/services/student-group-content.utils'
import { ContentListComponent } from './content-list/content-list.component'
import { BusySpinnerComponent } from '@monorepo/angular-common'
import { MatIcon } from '@angular/material/icon'

@Component({
  selector: 'app-ils-player',
  templateUrl: './ils-player.component.html',
  styleUrls: ['./ils-player.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TitleBarComponent,
    MatDrawerContainer,
    PhaseListComponent,
    AsyncPipe,
    TranslatePipe,
    MatDrawer,
    MatTooltip,
    MatIconButton,
    MatButton,
    NgClass,
    PhaseContentComponent,
    ContentListComponent,
    BusySpinnerComponent,
    MatIcon,
  ],
})
export class IlsPlayerComponent {
  ilsModelService = inject(IlsModelService)
  private readonly sendLogActionService = inject(SendLogActionService)
  private readonly cachingStorageService = inject(CachingStorageService)
  private readonly router = inject(Router)
  private readonly activatedRoute = inject(ActivatedRoute)
  private readonly tokenService = inject(TokenService)
  private readonly translateService = inject(TranslateService)
  private readonly titleService = inject(Title)
  private readonly studentGroupContentService = inject(StudentGroupContentService)

  @ViewChild('phasesDrawer') phasesDrawer: MatDrawer | undefined
  @ViewChild('globalAppsDrawer') globalAppsDrawer: MatDrawer | undefined
  previewMode = false
  showStudentGroupContent = false

  busyKey = ''

  constructor() {
    const tokenService = this.tokenService

    this.activatedRoute.paramMap.subscribe((params) => {
      if (params && params.has('ilsId')) {
        this.busyKey = 'general.actions.loadingIls'
        this.studentGroupContentService.setStudentGroupContent(null)
        this.showStudentGroupContent = false
        this.ilsModelService.initializeIls(params.get('ilsId') ?? '')
        const subscription = this.ilsModelService.ilsLoaded$.subscribe((loaded) => {
          if (loaded) {
            // console.log(tokenService.getUser())
            if (!this.checkPreviewModeMatchesUser()) {
              return
            }
            document.documentElement.lang = this.ilsModelService.language$.value
            this.translateService.use(this.ilsModelService.language$.value).subscribe(() => {
              this.translateService.get('twenteIlsPlayer').subscribe((ilsPlayerLabel) => {
                this.titleService.setTitle(
                  `${this.ilsModelService.serverPreTitle}${ilsPlayerLabel}: ${this.ilsModelService.title$.value}`,
                )
              })
            })
            this.cachingStorageService.initialize()
            this.sendLogActionService.initialize()
            this.sendLogActionService.sendEnterIlsAction()
            this.sendLogActionService.sendEnterPhaseAction()
            this.previewMode = this.ilsModelService.published
            this.loadStudentGroupContent()
            subscription.unsubscribe()
            this.busyKey = ''
          }
        })
      }
    })
  }

  get title(): string {
    return this.ilsModelService.title$.value
  }

  get globalContentList(): Content[] {
    return this.ilsModelService.globalContentList$.value
  }

  get showGlobalContentList(): boolean {
    return this.globalContentList.length > 0
  }

  togglePhasesDrawer() {
    if (!this.phasesDrawer) {
      return
    }
    this.phasesDrawer.toggle()
    this.ilsModelService.phaseListVisible$.next(this.phasesDrawer.opened)
    this.sendLogActionService.sendToggleIlsNavigationDrawerAction(this.phasesDrawer.opened)
  }

  toggleGlobalAppsDrawer() {
    if (!this.globalAppsDrawer) {
      return
    }
    this.globalAppsDrawer.toggle()
    this.ilsModelService.globalContentVisible$.next(this.globalAppsDrawer.opened)
    this.sendLogActionService.sendToggleIlsGlobalAppsDrawerAction(this.globalAppsDrawer.opened)
  }

  get hasNextPhase(): boolean {
    return this.ilsModelService.hasNextPhase
  }

  get hasPreviousPhase(): boolean {
    return this.ilsModelService.hasPreviousPhase
  }

  goToNextPhase() {
    if (!this.ilsModelService.hasNextPhase) {
      return
    }
    this.sendLogActionService.sendLeavePhaseAction()
    this.ilsModelService.goToNextPhase()
    this.sendLogActionService.sendEnterPhaseAction()
  }

  goToPreviousPhase() {
    if (!this.ilsModelService.hasPreviousPhase) {
      return
    }
    this.sendLogActionService.sendLeavePhaseAction()
    this.ilsModelService.goToPreviousPhase()
    this.sendLogActionService.sendEnterPhaseAction()
  }

  private checkPreviewModeMatchesUser(): boolean {
    if (this.ilsModelService.published !== this.tokenService.isPreviewUser()) {
      // the user preview mode does not match the ils preview mode
      // continuing to the ils player can cause account access issues
      // redirecting to the login page with the ilsId saved
      this.tokenService.signOut()
      this.router.navigateByUrl('/login/' + this.ilsModelService.ilsId)
      return false
    }
    return true
  }

  private async loadStudentGroupContent() {
    const studentId = this.tokenService.getUser()?.sub ?? ''
    if (!studentId) {
      this.studentGroupContentService.setStudentGroupContent(null)
      this.showStudentGroupContent = false
      return
    }

    try {
      const groupsInfo = await this.ilsModelService.getGroupsInfo()
      const studentGroupContent = createStudentGroupContent(groupsInfo, studentId)
      this.studentGroupContentService.setStudentGroupContent(studentGroupContent)
      this.showStudentGroupContent = !!studentGroupContent
    } catch (error) {
      console.error('Could not load student group content', error)
      this.studentGroupContentService.setStudentGroupContent(null)
      this.showStudentGroupContent = false
    }
  }

  protected readonly IlsStatus = IlsStatus
}
